import {
    faro,
    initializeFaro,
    ReactIntegration,
    getWebInstrumentations,
    LogLevel as FaroLogLevel
} from '@grafana/faro-react'
import { TracingInstrumentation } from '@grafana/faro-web-tracing'
import { LogLevel, type ObservabilityClientConfig, type ObservabilityMethods, type LogContext } from '../../types'

const LOG_LEVEL_MAP: Record<LogLevel, FaroLogLevel> = {
    [LogLevel.INFO]: FaroLogLevel.INFO,
    [LogLevel.WARN]: FaroLogLevel.WARN,
    [LogLevel.ERROR]: FaroLogLevel.ERROR
}

/**
 * Starts Faro.
 *
 * Call this from the app's `instrumentation-client.ts`, which Next runs after
 * the document loads and before React hydrates. A React provider runs later
 * than that, so it misses every error thrown while the app hydrates.
 *
 * An empty `collectorUrl` is how a local run and a preview without the variable
 * behave, not a failure. Faro would otherwise post every report to the app's
 * own origin. The log carries no identifier (ARCH-18).
 */
export function initializeObservability(config: ObservabilityClientConfig): void {
    if (typeof window === 'undefined' || faro.config) return

    if (!config.collectorUrl) {
        console.info('[Observability] Skipped initialization (no collector URL configured)')
        return
    }

    try {
        initializeFaro({
            url: config.collectorUrl,
            app: {
                name: config.app.name,
                version: config.app.version,
                environment: config.app.environment,
                namespace: config.app.namespace
            },
            instrumentations: [
                ...getWebInstrumentations(),
                new TracingInstrumentation({
                    // Same-origin requests carry `traceparent` already. Only a
                    // cross-origin API needs naming here, and its CORS policy
                    // must allow the header.
                    instrumentationOptions: {
                        propagateTraceHeaderCorsUrls: config.tracedApiUrls
                    }
                }),
                new ReactIntegration()
            ],
            beforeSend: config.beforeSend ?? undefined
        })
    } catch (error) {
        console.error('Failed to initialize observability:', error)
    }
}

export function createFaroLogMethods(): ObservabilityMethods {
    return {
        logWarning(message: string, context?: LogContext) {
            faro.api?.pushLog([message], { level: FaroLogLevel.WARN, context })
        },

        logError(message: string, context?: LogContext) {
            faro.api?.pushLog([message], { level: FaroLogLevel.ERROR, context })
        },

        logMeasurement(type: string, values: Record<string, number>, context?: LogContext, level?: LogLevel) {
            const stringifiedValues = Object.fromEntries(
                Object.entries(values).map(([key, value]) => [key, String(value)])
            )
            faro.api?.pushLog([type], {
                level: LOG_LEVEL_MAP[level ?? LogLevel.INFO],
                context: { ...context, ...stringifiedValues }
            })
        }
    }
}
